import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { normalizeEmail } from '../../utils/text';
import { fetchAirtableRecords, updateAirtableRecord } from '../../services/airtable';
import { sendApprovedRequestFilesToN8n, N8N_ARCHIVOS_WEBHOOK_URL } from '../../services/n8n';
import { useClientesDirectory } from '../../hooks/useClientesDirectory';
import { FALLBACK_CLIENTS_TABLE_ID } from '../../constants/formConfig';

const statusProgression = {
  pending: { key: 'review', label: 'En revisión' },
  review: { key: 'scheduled', label: 'Programado SAT' },
  scheduled: { key: 'sent', label: 'Publicado en SAT' },
  rejected: { key: 'review', label: 'En revisión' },
  sent: { key: 'sent', label: 'Publicado en SAT' }
};

const getNextStatus = currentKey => statusProgression[currentKey] || { key: 'review', label: 'En revisión' };

const serviceLabels = {
  NOM: 'NOM · Nómina',
  SUA: 'SUA · IMSS',
  'ISN/ISH': 'ISN/ISH · Impuestos Estatales',
  SAT: 'SAT · Portal SAT',
  DIOT: 'DIOT · Declaraciones informativas',
  CE: 'CE · Contabilidad electrónica',
  Otro: 'Otro servicio'
};

const MONTH_LABELS = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre'
];

const serviceUploadFields = {
  NOM: [
    { key: 'nomPeriod', label: 'Periodo', type: 'text', readOnly: true, periodLabel: true },
    { key: 'payrollDate', label: 'Fecha de nómina', type: 'date', required: true },
    { key: 'totalPayroll', label: 'Total dispersado', type: 'number', step: '0.01', placeholder: '0.00', required: true },
    { key: 'nomConfirmed', label: 'Confirmar envío de nómina', type: 'checkbox' }
  ],
  SUA: [
    { key: 'suaPeriod', label: 'Periodo', type: 'text', readOnly: true, periodLabel: true },
    { key: 'imssAmount', label: 'IMSS', type: 'number', step: '0.01', placeholder: '0.00', required: true },
    { key: 'rcvAmount', label: 'RCV', type: 'number', step: '0.01', placeholder: '0.00', required: true },
    { key: 'infonavitAmount', label: 'INFONAVIT', type: 'number', step: '0.01', placeholder: '0.00', required: true },
    { key: 'totalCuotas', label: 'Total cuotas', type: 'number', step: '0.01', placeholder: '0.00', readOnly: true },
    { key: 'dueDate', label: 'Vencimiento', type: 'date', required: true }
  ],
  'ISN/ISH': [
    { key: 'isnPeriod', label: 'Periodo', type: 'text', readOnly: true, periodLabel: true },
    { key: 'impSobreNom', label: 'Impuesto sobre nómina', type: 'number', step: '0.01', placeholder: '0.00', required: true },
    { key: 'empleados', label: 'Empleados / base gravable', type: 'number', step: '1', placeholder: '0', required: true },
    { key: 'dueDate', label: 'Vencimiento', type: 'date', required: true }
  ],
  SAT: [
    { key: 'satPeriod', label: 'Periodo', type: 'text', readOnly: true, periodLabel: true },
    { key: 'totalImpFederales', label: 'Total impuestos federales', type: 'number', step: '0.01', placeholder: '0.00', required: true },
    { key: 'dueDate', label: 'Vencimiento', type: 'date', required: true }
  ],
  DIOT: [
    { key: 'diotPeriod', label: 'Periodo', type: 'text', readOnly: true, periodLabel: true },
    { key: 'diotOperation', label: 'No. de operación', type: 'text', required: true },
    { key: 'diotType', label: 'Tipo de declaración', type: 'select', options: ['Normal', 'Complementaria'], required: true },
    { key: 'diotPresenta', label: 'La presenta', type: 'select', options: ['Con datos', 'En ceros'], required: true },
    { key: 'diotPresentationDate', label: 'Fecha de presentación', type: 'date', required: true },
    { key: 'diotIva', label: 'IVA declarado', type: 'number', step: '0.01', placeholder: '0.00', required: true },
    { key: 'dueDate', label: 'Vencimiento automático', type: 'date', readOnly: true, derived: 'nextMonth' }
  ],
  CE: [
    { key: 'cePeriod', label: 'Periodo', type: 'text', readOnly: true, periodLabel: true },
    { key: 'ceOperation', label: 'No. de operación', type: 'text', required: true },
    { key: 'ceType', label: 'Tipo de declaración', type: 'select', options: ['Normal', 'Complementaria'], required: true },
    { key: 'dueDate', label: 'Vencimiento automático', type: 'date', readOnly: true, derived: 'nextMonth' }
  ]
};

const serviceUploadPolicies = {
  NOM: { allowDriveUpload: false, maxFiles: 0 },
  SAT: { allowDriveUpload: true, maxFiles: 2 }
};

const getServiceFieldLabels = (service = '') => {
  const fields = serviceUploadFields[service] || [];
  return fields.reduce((map, field) => {
    map[field.key] = field.label;
    return map;
  }, {});
};

const formatPeriodLabel = period => {
  if (!period) return '';
  const [yearPart, monthPart] = period.split('-');
  const year = Number(yearPart);
  const month = Number(monthPart);
  if (!year || !month) return '';
  const monthLabel = MONTH_LABELS[month - 1] || monthPart;
  const formattedMonth = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);
  return `${formattedMonth} ${year}`;
};

const getLastDayOfNextMonth = period => {
  if (!period) return '';
  const [yearPart, monthPart] = period.split('-');
  const year = Number(yearPart);
  const month = Number(monthPart);
  if (!year || !month) return '';
  const date = new Date(Date.UTC(year, month, 1));
  date.setUTCMonth(date.getUTCMonth() + 2);
  date.setUTCDate(0);
  return date.toISOString().split('T')[0];
};

const formatDueDateLong = value => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  try {
    return new Intl.DateTimeFormat('es-MX', {
      dateStyle: 'full'
    }).format(parsed);
  } catch {
    return parsed.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
  }
};

const STEP_UPLOAD = 1;
const STEP_NOTIFICATION = 2;

const defaultActivityColumns = ['NOM', 'SUA', 'ISN/ISH', 'SAT'];
const clientAccentClasses = ['accent-a', 'accent-b', 'accent-c', 'accent-d', 'accent-e', 'accent-f'];
const NOTES_FIELD_ID = 'fldILbsanV5Bj3JHJ';
const ARCHIVOS_TABLE_ID = process.env.REACT_APP_AIRTABLE_ARCHIVOS_TABLE_ID || 'tblOTX32oPFJfpNZx';
const ARCHIVOS_FIELD_IDS = {
  calendario: 'fldIOVE8g0ziUfHJT',
  status: 'fld6oEkDvoLUVgo0k',
  uploadDate: 'fld5V7U7HTVdTwYhe'
};

const parseJsonNotes = value => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
};

const formatJsonPreviewValue = value => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (typeof value === 'number') return Number.isNaN(value) ? '' : String(value);
  if (typeof value === 'string') return value.trim();
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const formatJsonPreviewLabel = key => {
  if (!key) return '';
  const mapping = {
    nomPeriod: 'Periodo',
    suaPeriod: 'Periodo',
    isrPeriod: 'Periodo',
    payrollDate: 'Fecha de nómina',
    payrollPeriod: 'Periodo de nómina',
    dueDate: 'Fecha límite',
    imssAmount: 'Cuotas IMSS',
    rcvAmount: 'RCV',
    infonavitAmount: 'Infonavit',
    totalCuotas: 'Total cuotas',
    totalPayroll: 'Total nómina',
    impSobreNom: 'Impuesto sobre nómina',
    totalImpFederales: 'Impuestos federales',
    amount: 'Monto',
    total: 'Total',
    folio: 'Folio'
  };
  return mapping[key] || key;
};

const normalizeClientValue = value => {
  if (value === undefined || value === null) return '';
  return String(value).trim().toLowerCase();
};

const findClientInfoInDirectory = (task, directory = {}) => {
  if (!task || !directory) return null;
  const clientId = task.clientId || task.clienteAsociadoId;
  if (clientId && directory[clientId]) {
    return directory[clientId];
  }
  const candidates = [
    task.client,
    task.cliente,
    task.empresa,
    task.folder,
    task.metadata?.cliente,
    task.metadata?.empresa
  ]
    .map(normalizeClientValue)
    .filter(Boolean);
  if (!candidates.length) return null;
  return (
    Object.values(directory).find(info => {
      if (!info) return false;
      const haystack = [
        info.alias,
        info.empresa,
        info.nombreComercial,
        info.idCliente,
        info.name
      ]
        .map(normalizeClientValue)
        .filter(Boolean);
      return haystack.some(value => candidates.includes(value));
    }) || null
  );
};

const getClientAccentClass = value => {
  if (!value) {
    return clientAccentClasses[0];
  }
  const hash = String(value)
    .split('')
    .reduce((total, char) => total + char.charCodeAt(0), 0);
  return clientAccentClasses[hash % clientAccentClasses.length];
};

const getActivityTitle = (text = '', clientName = '', clientId = '') => {
  if (!text) return '';
  const parts = text.split(' - ').map(part => part.trim()).filter(Boolean);
  if (!parts.length) {
    return text;
  }
  const forbidden = [clientName, clientId]
    .map(value => (value || '').toLowerCase())
    .filter(Boolean);
  const filtered = parts.filter(part => {
    const lower = part.toLowerCase();
    if (forbidden.includes(lower)) return false;
    if (/^\d{4}-\d{2}(?:-\d{2})?$/.test(part)) return false;
    return true;
  });
  return filtered[0] || parts[0] || text;
};

const isAdminEmail = (email, directory = {}, userDirectory = {}) => {
  const normalized = normalizeEmail(email);
  const matchDirectory = Object.entries(directory).find(
    ([, info]) => normalizeEmail(info.correo) === normalized && info.rol === 'Administrador'
  );
  const matchUserDir = Object.entries(userDirectory).find(
    ([, info]) => normalizeEmail(info.correo) === normalized && info.rol && info.rol.toLowerCase() === 'administrador'
  );
  return Boolean(matchDirectory || matchUserDir) || /admin|direccion/.test(normalized);
};

const getAvailableScheduleYears = schedule => {
  if (!schedule) return [];
  const keys = Object.keys(schedule);
  const yearSet = new Set(keys.map(key => Number(key.split('-')[0])));
  return Array.from(yearSet.values());
};

const getDefaultYearValue = (years = [], schedule = {}) => {
  const todayYear = new Date().getFullYear();
  const scheduleYears = getAvailableScheduleYears(schedule);
  if (scheduleYears.includes(todayYear)) {
    return todayYear;
  }
  if (years.includes(todayYear)) {
    return todayYear;
  }
  return scheduleYears[0] || years[0] || todayYear;
};

const getDefaultMonthKey = (months = [], schedule = {}, year) => {
  const today = new Date();
  const currentKey = String(today.getMonth() + 1).padStart(2, '0');
  if (year && schedule[`${year}-${currentKey}`]) {
    return currentKey;
  }
  if (year) {
    const scheduledMonth = Object.keys(schedule).find(key => key.startsWith(`${year}-`));
    if (scheduledMonth) {
      return scheduledMonth.split('-')[1];
    }
  }
  const match = months.find(month => month.key === currentKey);
  return (match || months[0] || { key: '01' }).key;
};

function TaxUploadsBoard({
  summary = [],
  months = [],
  years = [],
  schedule = {},
  legend = [],
  checklist = [],
  activitySummary = {},
  activityLegend = [],
  userSession = null,
  responsablesDirectory = {},
  userDirectory = {},
  clientDirectory = {},
  calendarioTableId = ''
}) {
  const {
    clientDirectory: fetchedClientDirectory
  } = useClientesDirectory({
    enabled: !Object.keys(clientDirectory || {}).length,
    clientsTableId: FALLBACK_CLIENTS_TABLE_ID
  });
  const defaultYearValue = useMemo(
    () => getDefaultYearValue(years, schedule),
    [years, schedule]
  );
  const defaultMonthKey = useMemo(
    () => getDefaultMonthKey(months, schedule, defaultYearValue),
    [months, schedule, defaultYearValue]
  );

  const [selectedMonth, setSelectedMonth] = useState(defaultMonthKey);
  const [selectedYear, setSelectedYear] = useState(defaultYearValue);
  const [scheduleState, setScheduleState] = useState(schedule);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedClientSummary, setSelectedClientSummary] = useState(null);
  const [activityMatrixState, setActivityMatrixState] = useState(activitySummary);
  const [activeTaxView, setActiveTaxView] = useState('uploads');
  const [summaryFilterKey, setSummaryFilterKey] = useState('total');
  const [taskSearch, setTaskSearch] = useState('');
  const [selectedUploadClient, setSelectedUploadClient] = useState(null);
  const [notesTask, setNotesTask] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSyncState, setNotesSyncState] = useState('idle');
  const [notesError, setNotesError] = useState('');
  const notesSaveTimeoutRef = useRef(null);
  const notesRequestIdRef = useRef(0);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [isInfoCompact, setIsInfoCompact] = useState(false);
  const infoPanelPreferenceRef = useRef(false);
  const userEmail = normalizeEmail(userSession?.email);
  const isAdminUser = useMemo(
    () => isAdminEmail(userEmail, responsablesDirectory, userDirectory),
    [userEmail, responsablesDirectory, userDirectory]
  );
  const userRecordId = useMemo(() => {
    if (!userEmail) return '';
    const entry = Object.entries(userDirectory || {}).find(([, value]) => {
      const email = normalizeEmail(value?.correo);
      return email && email === userEmail;
    });
    return entry ? entry[0] : '';
  }, [userDirectory, userEmail]);
  const clientDirectoryResolved = useMemo(() => {
    const provided = clientDirectory && Object.keys(clientDirectory).length ? clientDirectory : null;
    if (provided) return provided;
    return fetchedClientDirectory || {};
  }, [clientDirectory, fetchedClientDirectory]);

  useEffect(() => {
    setScheduleState(schedule);
  }, [schedule]);

  useEffect(() => {
    setActivityMatrixState(activitySummary);
  }, [activitySummary]);

  useEffect(() => {
    setSelectedMonth(defaultMonthKey);
  }, [defaultMonthKey, activeTaxView]);

  useEffect(() => {
    setSelectedYear(defaultYearValue);
  }, [defaultYearValue]);

  useEffect(() => {
    setStatusFilter('all');
    setSelectedTask(null);
    setSelectedClientSummary(null);
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    setStatusFilter('all');
  }, [activeTaxView]);

  useEffect(() => {
    const handleResize = () => {
      setIsInfoCompact(window.innerWidth < 1100);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isInfoCompact) {
      setShowInfoPanel(false);
    } else {
      setShowInfoPanel(infoPanelPreferenceRef.current);
    }
  }, [isInfoCompact]);

  const showUploadView = activeTaxView === 'uploads';

  const scheduleKey = `${selectedYear}-${selectedMonth}`;
  const monthTasks = scheduleState[scheduleKey] || [];
  const matchesUserEmail = useCallback((emailsValue, rawValue) => {
    if (!userEmail && !userRecordId) return false;
    const toList = value => {
      if (Array.isArray(value)) return value;
      if (typeof value === 'string' && value.trim()) return [value];
      return [];
    };
    const emails = toList(emailsValue).map(normalizeEmail).filter(Boolean);
    const rawValues = toList(rawValue).filter(Boolean);
    const rawEmails = rawValues
      .filter(value => String(value).includes('@'))
      .map(normalizeEmail)
      .filter(Boolean);
    if (!emails.length && !rawValues.length) return false;
    return emails.includes(userEmail) ||
      rawEmails.includes(userEmail) ||
      (userRecordId && rawValues.includes(userRecordId));
  }, [userEmail, userRecordId]);
  const visibleMonthTasks = useMemo(() => {
    if (isAdminUser) {
      return monthTasks;
    }
    return monthTasks.filter(task => matchesUserEmail(task.responsableEmails, task.responsablesRaw));
  }, [isAdminUser, monthTasks, matchesUserEmail]);
  const filteredTasks = useMemo(() => {
    const term = taskSearch.trim().toLowerCase();
    return visibleMonthTasks.filter(task => {
      const matchStatus = statusFilter === 'all' ? true : task.dueStatus === statusFilter;
      if (!matchStatus) return false;
      let matchSummary = true;
      if (summaryFilterKey === 'pending') {
        matchSummary = ['dueSoon', 'late'].includes(task.dueStatus);
      } else if (summaryFilterKey === 'late') {
        matchSummary = task.dueStatus === 'late';
      }
      if (!matchSummary) return false;
      if (!term) return true;
      return [task.client, task.obligation, task.assignedTo, task.notes, task.folder]
        .some(value => (value || '').toLowerCase().includes(term));
    });
  }, [visibleMonthTasks, statusFilter, summaryFilterKey, taskSearch]);

  const activeMonth = months.find(month => month.key === selectedMonth);
  const tableSubtitle = activeMonth
    ? `${activeMonth.longLabel || activeMonth.label} ${selectedYear}`
    : `${selectedYear}`;
  const activityKey = `${selectedYear}-${selectedMonth}`;
  const monthActivities = activityMatrixState[activityKey] || [];
  const visibleActivities = useMemo(() => {
    if (isAdminUser) {
      return monthActivities;
    }
    return monthActivities.filter(row => matchesUserEmail(row.responsableEmails, row.responsablesRaw));
  }, [isAdminUser, monthActivities, matchesUserEmail]);
  const summaryFilteredActivities = useMemo(() => {
    const term = taskSearch.trim().toLowerCase();
    return visibleActivities.filter(row => {
      const matchTerm = term
        ? [row.client, row.shortName, row.clientId, (row.responsables || []).join(' ')]
          .concat(Object.values(row.obligations || {}).map(cell => cell.activity || ''))
          .some(value => (value || '').toLowerCase().includes(term))
        : true;
      const obligations = Object.values(row.obligations || {});
      const matchSummary = (() => {
        if (summaryFilterKey === 'pending') {
          return obligations.some(cell => ['dueSoon', 'late'].includes(cell.status));
        }
        if (summaryFilterKey === 'late') {
          return obligations.some(cell => cell.status === 'late');
        }
        return true;
      })();
      return matchTerm && matchSummary;
    });
  }, [visibleActivities, taskSearch, summaryFilterKey]);
  const monthHasActivities = monthActivities.length > 0;
  const hasHiddenActivities = monthHasActivities && !summaryFilteredActivities.length;
  const activityColumnKeys = useMemo(() => {
    const initial = [...defaultActivityColumns];
    const seen = new Set(initial);
    Object.values(activityMatrixState || {}).forEach(rows => {
      (rows || []).forEach(row => {
        Object.keys(row.obligations || {}).forEach(key => {
          if (!key) return;
          if (!seen.has(key)) {
            seen.add(key);
            initial.push(key);
          }
        });
      });
    });
    return initial;
  }, [activityMatrixState]);
  const viewLegend = showUploadView ? legend : activityLegend;
  const hasInfoPanel = showUploadView && Boolean(viewLegend.length || checklist.length);
  const boardClassName = `tax-board-grid tax-upload-grid ${!hasInfoPanel || !showInfoPanel ? 'info-collapsed' : ''}`;

  const handleUploadComplete = (task, uploadedFiles, metadata = {}, emailRecipients = []) => {
    const taskId = task?.id;
    const targetPeriod = task?.period || scheduleKey;
    const service = task?.service || '';
    const labelMap = getServiceFieldLabels(service);
    const metadataSummary = metadata && Object.keys(metadata).length
      ? Object.entries(metadata)
        .map(([key, value]) => `${labelMap[key] || key}: ${value}`)
        .join(' · ')
      : '';
    const fileSummary = uploadedFiles.length ? `Último PDF: ${uploadedFiles.join(', ')}` : '';
    const recipientSummary = emailRecipients.length
      ? `Destinatarios: ${emailRecipients.join(', ')}`
      : '';
    const combinedNotes = [
      task?.notes,
      metadataSummary ? `Datos capturados: ${metadataSummary}` : '',
      fileSummary,
      recipientSummary
    ]
      .filter(Boolean)
      .join(' | ');
    const nextStatusFromTask = getNextStatus(task?.statusKey);

    if (taskId && targetPeriod) {
      setScheduleState(prev => {
        const currentList = prev[targetPeriod] || [];
        if (!currentList.length) return prev;
        const next = { ...prev };
        next[targetPeriod] = currentList.map(item => {
          if (item.id !== taskId) {
            return item;
          }
          const nextStatus = getNextStatus(item.statusKey);
          return {
            ...item,
            statusKey: nextStatus.key,
            status: nextStatus.label,
            notes: combinedNotes || item.notes
          };
        });
        return next;
      });

      setSelectedUploadClient(prev => {
        if (!prev || prev.periodKey !== targetPeriod) return prev;
        return {
          ...prev,
          tasks: prev.tasks.map(item => {
            if (item.id !== taskId) return item;
            return {
              ...item,
              statusKey: nextStatusFromTask.key,
              status: nextStatusFromTask.label,
              notes: combinedNotes || item.notes
            };
          })
        };
      });
    }

    const activityPeriod = task?.activityPeriod || targetPeriod;
    const activityKeyToUpdate = task?.activityKey || service;
    const activityClientId = task?.activityClientId || task?.clientId || '';
    if (activityPeriod && activityKeyToUpdate && activityClientId) {
      setActivityMatrixState(prev => {
        const currentList = prev[activityPeriod] || [];
        if (!currentList.length) return prev;
        const next = { ...prev };
        next[activityPeriod] = currentList.map(item => {
          if (item.clientId !== activityClientId) return item;
          const currentCell = (item.obligations || {})[activityKeyToUpdate] || {};
          return {
            ...item,
            obligations: {
              ...item.obligations,
              [activityKeyToUpdate]: {
                ...currentCell,
                status: 'done'
              }
            }
          };
        });
        return next;
      });
    }
    setSelectedTask(null);
  };

  const syncNotesLocally = useCallback((targetTask, value, explicitPeriod) => {
    if (!targetTask) return;
    const targetPeriod = explicitPeriod || targetTask.period || targetTask.periodKey || scheduleKey;
    setScheduleState(prev => {
      const currentList = prev[targetPeriod] || [];
      if (!currentList.length) return prev;
      const nextList = currentList.map(item => {
        const matches = (targetTask.recordId && item.recordId === targetTask.recordId) || item.id === targetTask.id;
        return matches ? { ...item, notes: value } : item;
      });
      if (nextList === currentList) return prev;
      return {
        ...prev,
        [targetPeriod]: nextList
      };
    });
    setSelectedUploadClient(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        tasks: (prev.tasks || []).map(item => {
          const matches = (targetTask.recordId && item.recordId === targetTask.recordId) || item.id === targetTask.id;
          return matches ? { ...item, notes: value } : item;
        })
      };
    });
    setNotesTask(prev => {
      if (!prev) return prev;
      const matches = (targetTask.recordId && prev.recordId === targetTask.recordId) || prev.id === targetTask.id;
      return matches ? { ...prev, notes: value } : prev;
    });
  }, [scheduleKey]);

  const handleClientSelectFromUploads = task => {
    if (!task?.client) return;
    const tasksForClient = visibleMonthTasks
      .filter(item => item.client === task.client)
      .map(item => ({
        ...item,
        periodKey: scheduleKey
      }));
    if (!tasksForClient.length) return;
    setSelectedUploadClient({
      client: task.client,
      tasks: tasksForClient,
      timeframeLabel: tableSubtitle,
      periodKey: scheduleKey
    });
  };

  const handleOpenNotes = (task, periodKey = scheduleKey) => {
    if (!task) return;
    if (notesSaveTimeoutRef.current) {
      clearTimeout(notesSaveTimeoutRef.current);
      notesSaveTimeoutRef.current = null;
    }
    const resolvedPeriod = periodKey || task.periodKey || task.period || scheduleKey;
    setNotesTask({ ...task, periodKey: resolvedPeriod });
    setNotesDraft(task.notes || '');
    setNotesSyncState('idle');
    setNotesError('');
  };

  const handleCloseNotes = () => {
    if (notesSaveTimeoutRef.current) {
      clearTimeout(notesSaveTimeoutRef.current);
      notesSaveTimeoutRef.current = null;
    }
    setNotesTask(null);
    setNotesDraft('');
    setNotesSyncState('idle');
    setNotesError('');
  };

  const buildTaskFromActivity = (row, columnKey) => {
    const cell = row?.obligations?.[columnKey] || {};
    const periodKey = `${selectedYear}-${selectedMonth}`;
    const clientId = row.clientId || row.client || row.shortName || '';
    const cellResponsables = cell.responsables || [];
    return {
      id: `${clientId}-${columnKey}-${periodKey}`,
      period: periodKey,
      activityPeriod: periodKey,
      clientId: row.clientId || '',
      client: row.shortName || row.client || row.clientId || 'Cliente',
      rfc: row.clientId || '',
      obligation: columnKey,
      activity: cell.activity || columnKey,
      service: columnKey,
      assignedTo: cellResponsables.join(' / ') || (row.responsables || []).join(' / ') || 'Sin asignar',
      responsables: cellResponsables.length ? cellResponsables : (row.responsables || []),
      responsablesRaw: cell.responsablesRaw || row.responsablesRaw || [],
      responsableEmails: cell.responsableEmails || row.responsableEmails || [],
      requirements: [],
      folder: '',
      dueDate: cell.dueText || cell.dia || '',
      dueLabel: cell.dueText || cell.dia || '',
      dueText: cell.dueText || cell.dia || '',
      dueStatus: cell.status || 'pending',
      status: cell.status || 'Pendiente',
      statusKey: cell.status || 'pending',
      notes: cell.notes || '',
      recordId: cell.recordId || '',
      requiresUpload: Boolean(cell.requiresUpload),
      driveUrl: cell.driveUrl || '',
      source: 'activity',
      activityKey: columnKey,
      activityClientId: row.clientId || ''
    };
  };

  const handleActivityOpen = (row, columnKey) => {
    const cell = row?.obligations?.[columnKey];
    if (!cell || cell.status === 'na') return;
    setSelectedTask(buildTaskFromActivity(row, columnKey));
  };

  const handleSelectClient = row => {
    setSelectedClientSummary(row);
  };

  const handleToggleInfoPanel = () => {
    const nextState = !showInfoPanel;
    infoPanelPreferenceRef.current = nextState;
    setShowInfoPanel(nextState);
  };

  const handleOpenTaskFromModal = task => {
    setSelectedTask(task);
    setSelectedUploadClient(null);
  };

  const persistNotes = useCallback(async (value, targetTask) => {
    if (!targetTask || !targetTask.recordId || !calendarioTableId) {
      setNotesSyncState('error');
      setNotesError('No hay registro de Airtable vinculado a esta actividad.');
      return;
    }
    const requestId = ++notesRequestIdRef.current;
    try {
      await updateAirtableRecord({
        tableId: calendarioTableId,
        recordId: targetTask.recordId,
        fields: {
          [NOTES_FIELD_ID]: value
        }
      });
      if (notesRequestIdRef.current !== requestId) {
        return;
      }
      syncNotesLocally(targetTask, value, targetTask.periodKey);
      setNotesSyncState('saved');
      setNotesError('');
    } catch (error) {
      console.error('[Airtable] Error al actualizar notas internas', error);
      if (notesRequestIdRef.current !== requestId) {
        return;
      }
      setNotesSyncState('error');
      setNotesError(error.message || 'No se pudo guardar las notas.');
    }
  }, [calendarioTableId, syncNotesLocally]);

  useEffect(() => {
    if (!notesTask) {
      if (notesSaveTimeoutRef.current) {
        clearTimeout(notesSaveTimeoutRef.current);
        notesSaveTimeoutRef.current = null;
      }
      return;
    }
    if (!calendarioTableId || !notesTask.recordId) {
      setNotesSyncState('error');
      setNotesError('Este expediente no tiene registro vinculable.');
      return undefined;
    }
    if (notesDraft === (notesTask.notes || '')) {
      setNotesSyncState('idle');
      setNotesError('');
      if (notesSaveTimeoutRef.current) {
        clearTimeout(notesSaveTimeoutRef.current);
        notesSaveTimeoutRef.current = null;
      }
      return undefined;
    }
    if (notesSaveTimeoutRef.current) {
      clearTimeout(notesSaveTimeoutRef.current);
    }
    setNotesSyncState('saving');
    setNotesError('');
    const currentTask = notesTask;
    notesSaveTimeoutRef.current = setTimeout(() => {
      persistNotes(notesDraft, currentTask);
    }, 900);
    return () => {
      if (notesSaveTimeoutRef.current) {
        clearTimeout(notesSaveTimeoutRef.current);
        notesSaveTimeoutRef.current = null;
      }
    };
  }, [notesDraft, notesTask, persistNotes, calendarioTableId]);

  return (
    <section className="tax-view">
      <header className="tax-header">
        <p className="eyebrow">Impuestos</p>
        <h2>Evidencias fiscales</h2>
        <p>Seguimiento rápido de nómina, SUA, ISN/ISH y SAT con pendientes y responsables en un solo lugar.</p>
      </header>

      <div className="tax-summary-grid">
        {summary.map(card => (
          <article
            key={card.label}
            className={`tax-summary-card ${card.tone || ''} ${summaryFilterKey === (card.key || 'total') ? 'active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => setSummaryFilterKey(card.key || 'total')}
            onKeyPress={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                setSummaryFilterKey(card.key || 'total');
              }
            }}
          >
            <p className="label">{card.label}</p>
            <strong>{card.value}</strong>
            <span>{card.detail}</span>
          </article>
        ))}
      </div>

      <div className="tax-subtabs">
        <button
          type="button"
          className={`pill ${activeTaxView === 'uploads' ? 'active' : ''}`}
          onClick={() => setActiveTaxView('uploads')}
        >
          Evidencias
        </button>
        <button
          type="button"
          className={`pill ${activeTaxView === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTaxView('summary')}
        >
          Calendario
        </button>
      </div>

      <div className="tax-controls">
        <div className="tax-month-control">
          <p className="label">Mes de seguimiento</p>
          <div className="month-selector">
            {months.map(month => (
              <button
                key={month.key}
                type="button"
                className={`month-pill ${month.key === selectedMonth ? 'active' : ''}`}
                onClick={() => setSelectedMonth(month.key)}
              >
                {month.label}
              </button>
            ))}
          </div>
        </div>
        <div className="tax-year-select">
          <p className="label">Año fiscal</p>
          <select value={selectedYear} onChange={event => setSelectedYear(Number(event.target.value))}>
            {years.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div className="tax-search">
          <p className="label">Buscar expediente</p>
          <input
            type="text"
            placeholder="Cliente, servicio o responsable"
            value={taskSearch}
            onChange={event => setTaskSearch(event.target.value)}
          />
        </div>
        {hasInfoPanel && (
          <div className="tax-info-toggle">
            <p className="label">Panel de información</p>
            <button
              type="button"
              className={`info-toggle ${showInfoPanel ? 'active' : ''}`}
              onClick={handleToggleInfoPanel}
              aria-pressed={showInfoPanel}
            >
              {showInfoPanel ? 'Ocultar info' : 'Mostrar info'}
            </button>
            <small>{showInfoPanel ? 'Oculta' : 'Muestra'} semáforo y checklist</small>
          </div>
        )}
      </div>

      <div className={boardClassName}>
        <div className="tax-main-panel">
          {showUploadView ? (
            <TaxUploadTable
              tasks={filteredTasks}
              legend={legend}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              onOpenUpload={task => setSelectedTask(task)}
              totalTasks={visibleMonthTasks.length}
              timeframeLabel={tableSubtitle}
              onClientFocus={handleClientSelectFromUploads}
              onOpenNotes={handleOpenNotes}
              periodKey={scheduleKey}
            />
          ) : (
            <ActivitySummaryTable
              rows={summaryFilteredActivities}
              legend={activityLegend}
              onOpen={handleActivityOpen}
              onClientSelect={handleSelectClient}
              timeframeLabel={tableSubtitle}
              isAdmin={isAdminUser}
              columns={activityColumnKeys}
              hasHiddenRows={hasHiddenActivities}
            />
          )}
        </div>

        {showUploadView && showInfoPanel && hasInfoPanel && (
          <aside className="tax-side-panel">
            <StatusLegend legend={viewLegend} title="Semáforo de vencimientos" />

            {!!checklist.length && (
              <section className="tax-checklist">
                <h3>Checklist operativo</h3>
                <ul>
                  {checklist.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            )}
          </aside>
        )}
      </div>

      {activeTaxView === 'summary' && (
        <StatusLegend legend={activityLegend} title="Semáforo de actividades mensuales" />
      )}

      {selectedTask && (
        <UploadDialog
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onSubmit={handleUploadComplete}
          userSession={userSession}
          clientDirectoryResolved={clientDirectoryResolved}
          calendarioTableId={calendarioTableId}
        />
      )}

      {selectedClientSummary && (
        <ClientSummaryModal
          client={selectedClientSummary}
          onClose={() => setSelectedClientSummary(null)}
          timeframeLabel={tableSubtitle}
          onOpenActivity={(row, column) => handleActivityOpen(row, column)}
          columns={activityColumnKeys}
          isAdmin={isAdminUser}
        />
      )}

      {selectedUploadClient && (
        <ClientUploadsModal
          data={selectedUploadClient}
          onClose={() => setSelectedUploadClient(null)}
          timeframeLabel={selectedUploadClient.timeframeLabel}
          onOpenTask={handleOpenTaskFromModal}
          onOpenNotes={handleOpenNotes}
        />
      )}

      {notesTask && (
        <NotesDialog
          task={notesTask}
          value={notesDraft}
          onChange={setNotesDraft}
          onClose={handleCloseNotes}
          status={notesSyncState}
          errorMessage={notesError}
        />
      )}
    </section>
  );
}

function TaxUploadTable({
  tasks = [],
  legend = [],
  statusFilter = 'all',
  onStatusFilterChange,
  onOpenUpload,
  totalTasks = 0,
  timeframeLabel = '',
  onClientFocus,
  onOpenNotes,
  periodKey = ''
}) {
  const [expandedNoteIds, setExpandedNoteIds] = useState({});
  const filterOptions = useMemo(() => {
    const base = [{ key: 'all', label: 'Todos los semáforos' }];
    if (!legend.length) {
      return base;
    }
    return base.concat(legend.map(item => ({ key: item.key, label: item.label })));
  }, [legend]);
  const handleClientClick = task => {
    if (typeof onClientFocus === 'function') {
      onClientFocus(task);
    }
  };
  const handleNotesClick = task => {
    if (typeof onOpenNotes === 'function') {
      onOpenNotes(task, periodKey || undefined);
    }
  };
  const toggleNotesPreview = taskId => {
    if (!taskId) return;
    setExpandedNoteIds(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  return (
    <section className="tax-table-card tax-upload-table-card">
      <div className="section-header">
        <div>
          <h3>Expedientes</h3>
          <p>{timeframeLabel} · {totalTasks} expedientes</p>
        </div>
        <div className="tax-status-filters">
          {filterOptions.map(option => (
            <button
              key={option.key}
              type="button"
              className={`pill ${statusFilter === option.key ? 'active' : ''}`}
              onClick={() => onStatusFilterChange(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="tax-table tax-upload-table">
        {!tasks.length && (
          <div className="tax-table-empty">
            <p>No hay expedientes con ese semáforo.</p>
            <span className="helper-text">Selecciona otro filtro o cambia de mes.</span>
          </div>
        )}
        {tasks.map(task => {
          const requirements = Array.isArray(task.requirements) ? task.requirements : [];
          const hasRequirements = requirements.length > 0;
          const hasNotes = Boolean(task.notes);
          const parsedNotes = hasNotes ? parseJsonNotes(task.notes) : null;
          const jsonNoteEntries = parsedNotes
            ? Array.isArray(parsedNotes)
              ? parsedNotes.map((value, index) => ({ key: `${index + 1}`, value }))
              : Object.entries(parsedNotes).map(([key, value]) => ({ key, value }))
            : [];
          const isNotesExpanded = Boolean(expandedNoteIds[task.id]);
          const shouldClampJson = jsonNoteEntries.length > 2 && !isNotesExpanded;
          const visibleJsonEntries = shouldClampJson ? jsonNoteEntries.slice(0, 2) : jsonNoteEntries;
          const shouldClampText = hasNotes && !parsedNotes && !isNotesExpanded;
          const showNotesPreview = hasRequirements || hasNotes;
          const dueState = ['ontrack', 'dueSoon', 'late'].includes(task.dueStatus)
            ? task.dueStatus
            : 'default';
          return (
            <div key={task.id} className={`tax-table-row tax-upload-row status-${task.statusKey || 'pending'}`}>
              <div className="tax-upload-cell client-cell">
                <button
                  type="button"
                  className={`client-pill ${getClientAccentClass(task.client)}`}
                  onClick={() => handleClientClick(task)}
                >
                  <strong>{task.client}</strong>
                </button>
              </div>
              <div className="tax-upload-cell">
                <strong>{task.obligation}</strong>
                {task.folder && <span>{task.folder}</span>}
                {task.service && (
                  <span className="service-pill">
                    {serviceLabels[task.service] || task.service}
                  </span>
                )}
              </div>
              <div className="tax-upload-cell responsable-cell">
                <strong>{task.assignedTo || 'Sin asignar'}</strong>
              </div>
              <div className={`tax-upload-cell note-preview-cell ${showNotesPreview ? '' : 'no-content'}`}>
                {showNotesPreview && (
                  <>
                    {hasRequirements && (
                      <ul className="requirement-list">
                        {requirements.map(item => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    )}
                    {hasNotes && parsedNotes && jsonNoteEntries.length > 0 ? (
                      <ul className="note-json-list">
                        {visibleJsonEntries.map(entry => (
                          <li key={entry.key}>
                            <strong>{formatJsonPreviewLabel(entry.key)}</strong>
                            <span>{formatJsonPreviewValue(entry.value)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      hasNotes && (
                        <small className={`note-text ${shouldClampText ? 'is-clamped' : ''}`}>
                          {task.notes}
                        </small>
                      )
                    )}
                    {hasNotes && (jsonNoteEntries.length > 2 || (!parsedNotes && task.notes.length > 0)) && (
                      <button
                        type="button"
                        className="link-btn note-preview-toggle"
                        onClick={() => toggleNotesPreview(task.id)}
                      >
                        {isNotesExpanded ? 'Ver menos' : 'Ver más'}
                      </button>
                    )}
                  </>
                )}
              </div>
              <div className={`tax-status-indicator ${dueState}`}>
                <span
                  className={`tax-status-chip ${dueState}`}
                  title={task.status}
                  aria-label={task.status || dueState}
                />
              </div>
              <div className={`traffic-chip ${task.dueStatus}`}>
                <strong>{task.dueLabel}</strong>
                <small>{task.dueText}</small>
              </div>
              <div className="tax-upload-actions">
                <button type="button" className="btn secondary" onClick={() => onOpenUpload(task)}>
                  Abrir
                </button>
              </div>
              <div className="tax-notes-toggle">
                <button
                  type="button"
                  className={`note-icon-button ${task.notes ? 'active' : ''}`}
                  aria-label={task.notes ? 'Editar notas' : 'Agregar notas'}
                  onClick={() => handleNotesClick(task)}
                >
                  <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
                    <path
                      d="M6 3h12a1 1 0 0 1 1 1v16.5a.5.5 0 0 1-.85.35L15 18H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm1 6.5h10m-10-3h10"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ActivitySummaryTable({
  rows = [],
  legend = [],
  onOpen,
  onClientSelect,
  timeframeLabel = '',
  isAdmin = false,
  columns = defaultActivityColumns,
  hasHiddenRows = false
}) {
  const gridTemplate = useMemo(() => {
    const base = `1.4fr repeat(${columns.length}, minmax(0, 1fr))`;
    return isAdmin ? `${base} 1fr` : base;
  }, [columns.length, isAdmin]);
  const gridStyle = useMemo(() => ({ '--activity-grid': gridTemplate }), [gridTemplate]);

  return (
    <section className="tax-table-card activity-summary-card">
      <div className="section-header">
        <div>
          <h3>Calendario mensual</h3>
          <p>{timeframeLabel} · {rows.length} clientes</p>
        </div>
        <div className="activity-legend-inline">
          {legend.map(item => (
            <span key={item.key} className="activity-legend-chip">
              <span className={`status-dot ${item.key}`} />
              {item.label}
            </span>
          ))}
        </div>
      </div>

      <div className="activity-table">
        <div className="activity-head" style={gridStyle}>
          <span>Cliente</span>
          {columns.map(column => (
            <span key={column}>{column}</span>
          ))}
          {isAdmin && <span>Responsable</span>}
        </div>

        {!rows.length && (
          <div className="tax-table-empty">
            {hasHiddenRows ? (
              <>
                <p>No hay actividades visibles con los filtros actuales.</p>
                <span className="helper-text">Quita el filtro de semáforo o cambia de vista para consultarlas.</span>
              </>
            ) : (
              <>
                <p>No hay actividades asignadas en este mes para tu usuario.</p>
                <span className="helper-text">Revisa otro mes o solicita acceso al administrador.</span>
              </>
            )}
          </div>
        )}

        {rows.map(row => {
          const clientName = row.shortName || row.client || row.clientId || '';
          const clientIdentifier = row.clientId || row.client || '';
          return (
            <div
              key={row.clientId || row.client}
              className="activity-row"
              style={gridStyle}
            >
              <button type="button" className="client-cell" onClick={() => onClientSelect(row)}>
                <strong>{row.shortName || row.client}</strong>
                <small>{row.client}</small>
              </button>
              {columns.map(column => {
                const cell = row.obligations?.[column] || {};
                const status = cell.status || 'pending';
                const label = getActivityTitle(cell.activity || column || '', clientName, clientIdentifier);
                return (
                  <div key={`${row.clientId || row.client}-${column}`} className={`activity-cell ${cell && cell.status ? '' : 'disabled-cell'}`}>
                    <div className={`activity-chip ${status} ${cell && cell.status ? '' : 'muted'}`}>
                      <strong>{label}</strong>
                      <small>{cell.dueText || cell.dia || ''}</small>
                    </div>
                    <button
                      type="button"
                      className="btn ghost"
                      disabled={status === 'na' || !cell || !cell.status}
                      onClick={() => cell && cell.status && onOpen(row, column)}
                    >
                      Abrir
                    </button>
                  </div>
                );
              })}
              {isAdmin && (
                <div className="activity-responsable">
                  <strong>{(row.responsables || []).join(' / ') || 'N/D'}</strong>
                  <small>Vista de administrador</small>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ClientSummaryModal({
  client,
  onClose,
  timeframeLabel = '',
  onOpenActivity,
  columns = defaultActivityColumns,
  isAdmin = false
}) {
  if (!client) return null;
  const clientName = client.shortName || client.client || client.clientId || '';

  return (
    <div className="overlay activity-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="activity-modal" onClick={event => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="eyebrow">Cliente seleccionado</p>
            <h3>{client.shortName || client.client}</h3>
            <p className="subtitle">{timeframeLabel}</p>
          </div>
          <button type="button" className="link-btn" onClick={onClose}>
            Cerrar
          </button>
        </div>

        <div className="activity-modal-body">
          <div className="activity-modal-grid">
            <div className="activity-modal-row">
              <div className="client-cell">
                <strong>{clientName}</strong>
                <small>{client.clientId ? '' : ''}</small>
              </div>
              {columns.map(column => {
                const cell = client.obligations?.[column] || {};
                const status = cell.status || '';
                const isDisabled = !cell || !cell.status || cell.status === 'na';
                if (isDisabled) return null;
                const label = getActivityTitle(cell.activity || column, clientName, client.clientId || client.client);
                return (
                  <div key={`${client.client}-${column}`} className={`activity-chip condensed ${status}`}>
                    <strong>{label}</strong>
                    <small>{cell.dueText || cell.dia || ''}</small>
                    {typeof onOpenActivity === 'function' && (
                      <button
                        type="button"
                        className="btn secondary"
                        onClick={() => onOpenActivity(client, column)}
                      >
                        Abrir
                      </button>
                    )}
                  </div>
                );
              })}
              {isAdmin && (
                <div className="activity-responsable">
                  <strong>{(client.responsables || []).join(' / ') || 'N/D'}</strong>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClientUploadsModal({
  data,
  onClose,
  timeframeLabel = '',
  onOpenTask,
  onOpenNotes
}) {
  if (!data) return null;
  const { client, tasks = [] } = data;
  const responsables = Array.from(
    new Set(tasks.map(task => (task.assignedTo || '').trim()).filter(Boolean))
  );
  const clientSubtitle = tasks.length > 1
    ? `${tasks.length} expedientes`
    : (tasks[0]?.obligation || '');
  const handleOpenTask = task => {
    if (typeof onOpenTask === 'function') {
      onOpenTask(task);
    }
  };
  const handleOpenNotes = task => {
    if (typeof onOpenNotes === 'function') {
      onOpenNotes(task, task.periodKey);
    }
  };

  return (
    <div className="overlay activity-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="activity-modal" onClick={event => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="eyebrow">Cliente seleccionado</p>
            <h3>{client}</h3>
            <p className="subtitle">{timeframeLabel}</p>
          </div>
          <button type="button" className="link-btn" onClick={onClose}>
            Cerrar
          </button>
        </div>

        <div className="activity-modal-body">
          <div className="activity-modal-grid">
            <div className="activity-modal-row">
              <div className="client-cell">
                <strong>{client}</strong>
                <small>{clientSubtitle}</small>
              </div>
              {tasks.map(task => (
                <div key={task.id} className={`activity-chip condensed ${task.dueStatus || 'pending'}`}>
                  <strong>{`${task.obligation} - ${client} - ${task.dueLabel || task.dueText || ''}`}</strong>
                  <small>{task.folder || task.dueText || ''}</small>
                  <div className="chip-actions">
                    <button type="button" className="btn secondary" onClick={() => handleOpenTask(task)}>
                      Abrir
                    </button>
                    <button type="button" className="icon-btn note" onClick={() => handleOpenNotes(task)} aria-label="Notas del expediente">
                      <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
                        <path
                          d="M6 3h12a1 1 0 0 1 1 1v16.5a.5.5 0 0 1-.85.35L15 18H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm1 6.5h10m-10-3h10"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
              <div className="activity-responsable">
                <strong>{responsables.length ? responsables.join(' / ') : 'Sin responsable'}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusLegend({ legend = [], title = 'Estados de seguimiento' }) {
  if (!legend.length) {
    return null;
  }

  return (
    <section className="tax-legend">
      <h3>{title}</h3>
      <ul>
        {legend.map(item => (
          <li key={item.key}>
            <span className={`status-dot ${item.key}`} />
            <div>
              <strong>{item.label}</strong>
              <p>{item.description}</p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function NotesDialog({ task, value, onChange, onClose, status = 'idle', errorMessage = '' }) {
  if (!task) return null;
  const requirements = Array.isArray(task.requirements) ? task.requirements : [];
  const handleChange = event => {
    if (typeof onChange === 'function') {
      onChange(event.target.value);
    }
  };
  const isReadOnly = !task.recordId;
  const renderStatusText = () => {
    if (isReadOnly) {
      return 'Este expediente no está vinculado a Airtable.';
    }
    if (status === 'saving') return 'Guardando...';
    if (status === 'saved') return 'Cambios guardados.';
    if (status === 'error') return errorMessage || 'No se pudo guardar esta nota.';
    return 'Se guarda automáticamente.';
  };
  const statusClass = `note-status ${status}${isReadOnly ? ' note-status-error' : ''}`;

  return (
    <div className="overlay notes-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="notes-modal" onClick={event => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="eyebrow">Notas del expediente</p>
            <h3>{task.client}</h3>
            <p className="subtitle">{task.obligation}</p>
          </div>
          <button type="button" className="link-btn" onClick={onClose}>
            Cerrar
          </button>
        </div>

        <div className="notes-modal-body">
          {!!requirements.length && (
            <section className="note-requirements">
              <h4>Requisitos</h4>
              <ul>
                {requirements.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          )}
          <label htmlFor="note-field">Notas internas</label>
          <textarea
            id="note-field"
            value={value}
            onChange={handleChange}
            readOnly={isReadOnly}
            placeholder="Añade notas o recordatorios para este expediente."
          />
          <p className={statusClass}>{renderStatusText()}</p>
        </div>
      </div>
    </div>
  );
}

function UploadDialog({
  task,
  onClose,
  onSubmit,
  userSession,
  clientDirectoryResolved = {},
  calendarioTableId = ''
}) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [metadata, setMetadata] = useState({});
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState('');
  const [isSendingEvidence, setIsSendingEvidence] = useState(false);
  const [activeStep, setActiveStep] = useState(STEP_UPLOAD);
  const [showLegacyUploadSection, setShowLegacyUploadSection] = useState(false);
  const serviceFields = serviceUploadFields[task?.service] || [];
  const serviceFieldLabels = useMemo(() => getServiceFieldLabels(task?.service), [task?.service]);
  const serviceKey = (task?.service || '').toUpperCase();
  const uploadPolicy = serviceUploadPolicies[serviceKey] || { allowDriveUpload: true, maxFiles: null };
  const allowsDriveUpload = uploadPolicy.allowDriveUpload !== false;
  const maxFilesAllowed =
    Number.isFinite(uploadPolicy.maxFiles) && uploadPolicy.maxFiles > 0 ? uploadPolicy.maxFiles : null;
  const requiresUpload = Boolean(task?.requiresUpload) && allowsDriveUpload;
  const effectiveClientDirectory = clientDirectoryResolved || {};
  const isSatService = serviceKey === 'SAT';
  const isTaskDone =
    String(task?.statusKey || task?.dueStatus || task?.status || '')
      .toLowerCase()
      .trim() === 'done';
  const isStatusCompletado = String(task?.estatus || task?.status || '')
    .toLowerCase()
    .includes('completado');
  const isTaskCompleted = isTaskDone || isStatusCompletado;
  const hasMetadataValue = value => {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (typeof value === 'number') return !Number.isNaN(value);
    if (typeof value === 'boolean') return value;
    return Boolean(value);
  };
  const totalSatValue = Number(metadata.totalImpFederales);
  const isSatZeroTaxes = isSatService && !Number.isNaN(totalSatValue) && totalSatValue <= 0;
  const baseDueDate =
    task?.dueDate ||
    task?.dueText ||
    task?.dueLabel ||
    '';
  const legacyDueDate =
    metadata.dueDateIsn ||
    metadata.dueDateSat ||
    metadata.diotDueDate ||
    metadata.ceDueDate ||
    '';
  const resolvedDueDate =
    isSatZeroTaxes && !hasMetadataValue(metadata.dueDate)
      ? ''
      : (metadata.dueDate || legacyDueDate || baseDueDate);
  const showInternalNotesSummary = isTaskDone || isStatusCompletado;
  const canEditFields = !showInternalNotesSummary;
  const resolvedDueDateLong = formatDueDateLong(resolvedDueDate) || resolvedDueDate;
  const folderUrl = task?.driveUrl || '';
  const folderLabel = task?.folder || '';
  const folderDisplay = folderUrl || folderLabel;

  const parsedInternalNotes = useMemo(() => {
    if (!task?.notes || typeof task.notes !== 'string') return null;
    const trimmed = task.notes.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
    try {
      const parsed = JSON.parse(trimmed);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }, [task?.notes]);

  const formatNoteValue = (value, key = '') => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';
    if (typeof value === 'number') {
      if (/amount|total|monto|cuotas|impuesto/i.test(key)) {
        return value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return String(value);
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return formatDueDateLong(trimmed) || trimmed;
      }
      if (/amount|total|monto|cuotas|impuesto/i.test(key) && !Number.isNaN(Number(trimmed))) {
        return Number(trimmed).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return trimmed;
    }
    return String(value);
  };

  const formattedInternalNotes = useMemo(() => {
    if (!parsedInternalNotes || Array.isArray(parsedInternalNotes)) return [];
    const normalizeNoteKey = key => {
      if (['dueDateSat', 'dueDateIsn', 'diotDueDate', 'ceDueDate'].includes(key)) {
        return 'dueDate';
      }
      return key;
    };
    return Object.entries(parsedInternalNotes).map(([key, value]) => {
      const normalizedKey = normalizeNoteKey(key);
      return {
        key,
        label: serviceFieldLabels[normalizedKey] || serviceFieldLabels[key] || key,
        value: formatNoteValue(value, normalizedKey)
      };
    }).filter(item => item.value !== '');
  }, [parsedInternalNotes, serviceFieldLabels]);

  useEffect(() => {
    setSelectedFiles([]);
    setFeedback('');
    setIsDragging(false);
    setActiveStep(STEP_UPLOAD);
    const defaults = serviceFields.reduce((acc, field) => {
      let initial = '';
      if (field.type === 'checkbox') {
        initial = false;
      }
      if (field.readOnly) {
        if (field.periodLabel) {
          initial = formatPeriodLabel(task?.period || '');
        } else if (field.derived === 'nextMonth') {
          initial = getLastDayOfNextMonth(task?.period);
        }
      }
      acc[field.key] = initial;
      return acc;
    }, {});
    setMetadata(defaults);
    setEmailRecipients([]);
    setNewRecipient('');
    setShowLegacyUploadSection(false);
  }, [task, serviceFields]);

  useEffect(() => {
    if (!showInternalNotesSummary || !parsedInternalNotes || !serviceFields.length) return;
    setMetadata(prev => {
      let changed = false;
      const next = { ...prev };
      serviceFields.forEach(field => {
        const candidateKeys = field.key === 'dueDate'
          ? [field.key, 'dueDateSat', 'dueDateIsn', 'diotDueDate', 'ceDueDate']
          : [field.key];
        const matchedKey = candidateKeys.find(key => key in parsedInternalNotes);
        if (!matchedKey) return;
        const rawValue = parsedInternalNotes[matchedKey];
        let value = rawValue;
        if (field.type === 'checkbox') {
          value = rawValue === true || rawValue === 'true' || rawValue === 1 || rawValue === '1';
        }
        if (prev[field.key] !== value) {
          next[field.key] = value;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [showInternalNotesSummary, parsedInternalNotes, serviceFields]);

  useEffect(() => {
    if (task?.service !== 'SUA') return;
    const imss = Number(metadata.imssAmount) || 0;
    const rcv = Number(metadata.rcvAmount) || 0;
    const infonavit = Number(metadata.infonavitAmount) || 0;
    const total = (imss + rcv + infonavit).toFixed(2);
    setMetadata(prev => (prev.totalCuotas === total ? prev : { ...prev, totalCuotas: total }));
  }, [task?.service, metadata.imssAmount, metadata.rcvAmount, metadata.infonavitAmount]);

  useEffect(() => {
    if (!task) return;
    const nextDue = getLastDayOfNextMonth(task.period);
    if (!nextDue) return;
    if (task.service === 'DIOT' || task.service === 'CE') {
      setMetadata(prev => (prev.dueDate === nextDue ? prev : { ...prev, dueDate: nextDue }));
    }
  }, [task]);

  const handleFiles = files => {
    if (!canEditFields) return;
    if (!allowsDriveUpload) {
      setFeedback('Esta obligación no admite archivos en Drive.');
      return;
    }
    if (!files?.length) {
      return;
    }
    const incoming = Array.from(files);
    const merged = [...selectedFiles, ...incoming];
    const deduped = [];
    const seen = new Set();
    merged.forEach(file => {
      const key = `${file.name}-${file.size || 0}-${file.lastModified || 0}`;
      if (seen.has(key)) return;
      seen.add(key);
      deduped.push(file);
    });
    const maxFiles = maxFilesAllowed || deduped.length;
    const limitedFiles = deduped.slice(0, maxFiles);
    if (maxFilesAllowed && deduped.length > maxFiles) {
      if (isSatService) {
        setFeedback('SAT permite hasta 2 archivos. Se conservaron los primeros 2.');
      } else {
        setFeedback(`Solo se permiten ${maxFiles} archivos. Se conservaron los primeros ${maxFiles}.`);
      }
    } else {
      setFeedback('');
    }
    setSelectedFiles(limitedFiles);
  };

  const handleRemoveFile = indexToRemove => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    setFeedback('');
  };

  const handleFileInput = event => {
    const files = Array.from(event.target.files || []);
    handleFiles(files);
  };

  const handleDragOver = event => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = event => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = event => {
    event.preventDefault();
    setIsDragging(false);
    if (!canEditFields) return;
    const files = Array.from(event.dataTransfer?.files || []);
    handleFiles(files);
  };

  const handleMetadataChange = (fieldKey, value) => {
    setMetadata(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const handleAddRecipient = () => {
    const value = (newRecipient || '').trim();
    if (!value) return;
    if (emailRecipients.includes(value)) {
      setNewRecipient('');
      return;
    }
    setEmailRecipients(prev => [...prev, value]);
    setNewRecipient('');
  };

  const handleRemoveRecipient = email => {
    setEmailRecipients(prev => prev.filter(item => item !== email));
  };

  const buildEvidenceRequestPayload = (extra = {}) => {
    const formInputs = serviceFields.map(field => ({
      key: field.key,
      label: field.label,
      value: metadata[field.key] ?? '',
      type: field.type
    }));
    const period = task?.period || '';
    const totalValue =
      metadata.totalCuotas ||
      metadata.totalImpFederales ||
      metadata.totalPayroll ||
      metadata.impSobreNom ||
      '';
    const folioBase =
      task?.recordId || `${task?.client || 'cliente'}-${task?.service || 'servicio'}-${period}`;
    const directoryFallback = effectiveClientDirectory;
    const directoryInfo =
      task?.clienteAsociadoInfo ||
      (task?.clientId && directoryFallback[task.clientId]) ||
      findClientInfoInDirectory({ ...task, metadata }, directoryFallback);
    const listaCtesIdCliente = task?.listaCtesIdCliente || directoryInfo?.idCliente || '';
    const empresaValue = task?.empresa || directoryInfo?.empresa || task?.folder || task?.client;
    const folderLabel =
      listaCtesIdCliente && empresaValue
        ? `${empresaValue} - ${listaCtesIdCliente}`
        : task?.folder || empresaValue;
    const clienteAsociadoId = directoryInfo?.id || task?.clientId || task?.clienteAsociadoId;
    const activityLabel = task?.activity || task?.obligation || task?.service || '';
    const activityPeriod = task?.activityPeriod || period;
    const activityKey = task?.activityKey || task?.service || task?.obligation || '';
    const dueTextValue = task?.dueText || task?.dueLabel || resolvedDueDate || '';
    const fileStatus = isTaskCompleted && requiresUpload ? 'Vigente' : '';

    return {
      folio: folioBase,
      cliente: task?.client,
      empresa: empresaValue,
      total: totalValue,
      responsable: task?.assignedTo,
      clienteAsociadoId,
      listaCtesIdCliente,
      service: task?.service,
      obligation: task?.obligation,
      activity: activityLabel,
      activityKey,
      activityPeriod,
      activityId: task?.id,
      period,
      folder: folderLabel,
      dueDate: resolvedDueDate,
      dueLabel: task?.dueLabel || dueTextValue || resolvedDueDate,
      dueText: dueTextValue,
      requiresUpload,
      uploadPolicy: {
        allowDriveUpload: allowsDriveUpload,
        maxFiles: maxFilesAllowed
      },
      status: task?.status,
      statusKey: task?.statusKey,
      serviceFieldLabels,
      formInputs,
      emailRecipients,
      notificationPreview: buildEmailPreview(),
      assignedTo: task?.assignedTo,
      clienteAsociadoInfo: directoryInfo || null,
      metadata,
      fileStatus,
      airtableRecordId: task?.recordId,
      calendarioRecordId: task?.recordId,
      calendarioTableId,
      clientId: task?.clientId,
      requirements: task?.requirements,
      notes: task?.notes,
      ...extra
    };
  };

  const fetchLatestArchivoStatus = async () => {
    if (!task?.recordId) return null;
    try {
      const records = await fetchAirtableRecords({
        tableId: ARCHIVOS_TABLE_ID,
        maxRecords: 1,
        filterByFormula: `FIND('${task.recordId}', ARRAYJOIN({${ARCHIVOS_FIELD_IDS.calendario}}))`,
        fields: [ARCHIVOS_FIELD_IDS.status, ARCHIVOS_FIELD_IDS.uploadDate],
        returnFieldsByFieldId: true,
        sort: [{ field: ARCHIVOS_FIELD_IDS.uploadDate, direction: 'desc' }]
      });
      const record = records[0];
      if (!record) return null;
      return {
        id: record.id,
        status: record.fields?.[ARCHIVOS_FIELD_IDS.status] || ''
      };
    } catch (error) {
      console.warn('[Airtable] No se pudo leer estado de Archivos:', error);
      return null;
    }
  };

  const validateStepOne = () => {
    if (!canEditFields) {
      return { valid: false, message: 'Expediente completado. Solo lectura.' };
    }
    if (requiresUpload && showLegacyUploadSection && !selectedFiles.length) {
      return { valid: false, message: 'Adjunta al menos un archivo PDF para esta actividad.' };
    }
    const missingField = serviceFields.find(field => {
      if (!field.required) return false;
      if (field.key === 'dueDate' && isSatZeroTaxes) return false;
      return !hasMetadataValue(metadata[field.key]);
    });
    if (missingField) {
      return { valid: false, message: `Completa ${missingField.label}.` };
    }
    return { valid: true };
  };

  const handleContinueToNotification = async () => {
    const validation = validateStepOne();
    if (!validation.valid) {
      setFeedback(validation.message);
      return;
    }
    setFeedback('');
    if (isSendingEvidence) {
      return;
    }
    const latestArchivoStatus = await fetchLatestArchivoStatus();
    const evidencePayload = buildEvidenceRequestPayload({
      existingFileStatus: latestArchivoStatus?.status || '',
      existingFileRecordId: latestArchivoStatus?.id || ''
    });
    try {
      setIsSendingEvidence(true);
      await sendApprovedRequestFilesToN8n({
        files: selectedFiles,
        request: evidencePayload,
        webhookUrl: N8N_ARCHIVOS_WEBHOOK_URL,
        triggeredBy: userSession?.email || userSession?.displayName || 'dashboard',
        executedAt: new Date().toISOString()
      });
      setFeedback('Evidencia enviada a n8n, continúa con la notificación.');
      setActiveStep(STEP_NOTIFICATION);
    } catch (error) {
      console.error('[n8n] Error al enviar la evidencia:', error);
      setFeedback(error?.message || 'No fue posible enviar la evidencia a n8n.');
    } finally {
      setIsSendingEvidence(false);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (activeStep === STEP_UPLOAD) {
      await handleContinueToNotification();
      return;
    }
    if (!emailRecipients.length) {
      setFeedback('Agrega al menos un destinatario para este aviso.');
      return;
    }
    const fileNames = selectedFiles.map(file => file.name);
    if (typeof onSubmit === 'function') {
      onSubmit(task, fileNames, metadata, emailRecipients);
    }
  };

  const buildEmailPreview = () => {
    const clientName = task?.client || '';
    const empresa = task?.client || '';
    const periodLabel =
      metadata.nomPeriod ||
      metadata.suaPeriod ||
      metadata.isnPeriod ||
      metadata.satPeriod ||
      metadata.diotPeriod ||
      metadata.cePeriod ||
      task?.period ||
      '';
    const dueDateLong = formatDueDateLong(resolvedDueDate) || resolvedDueDate;

    if (task?.service === 'SUA') {
      const imss = metadata.imssAmount || '0.00';
      const rcv = metadata.rcvAmount || '0.00';
      const infonavit = metadata.infonavitAmount || '0.00';
      const total = metadata.totalCuotas || '0.00';
      return [
        `Estimado, ${clientName}`,
        '',
        `Enviamos el link de la línea de captura del SUA del periodo ${periodLabel} de la empresa ${empresa} con un total de ${total}:`,
        '',
        `"Cuotas IMSS ${imss}"`,
        `"Cuotas RCV ${rcv}"`,
        `"Cuotas INFONAVIT ${infonavit}"`,
        `"TOTAL Cuotas SUA ${total}"`,
        '',
        `Fecha de vencimiento: ${dueDateLong || resolvedDueDate}`,
        '',
        'Para cualquier duda puedes ponerte en contacto con nosotros en el teléfono o si lo deseas puedes escribirnos al correo.',
        '',
        'https://link.'
      ].join('\\n');
    }

    if (task?.service === 'ISN/ISH') {
      const totalIsn = metadata.impSobreNom || '0.00';
      return [
        `Estimado, ${clientName}`,
        '',
        `Enviamos el link del formato o referencia para pago del ISN del periodo ${periodLabel} de la empresa ${empresa} con un total de ${totalIsn}:`,
        '',
        `"TOTAL Impuesto Estatal ISN ${totalIsn}"`,
        '',
        `Fecha de vencimiento: ${dueDateLong || resolvedDueDate}`,
        '',
        'Para cualquier duda puedes ponerte en contacto con nosotros en el teléfono o si lo deseas puedes escribirnos al correo.',
        '',
        'https://link.'
      ].join('\\n');
    }

    if (task?.service === 'SAT') {
      const totalSat = metadata.totalImpFederales || '0.00';
      return [
        `Estimado, ${clientName}`,
        '',
        `Enviamos el link de la o las referencias para pago de Impuestos Federales (SAT) del periodo ${periodLabel} de la empresa ${empresa} con un total de ${totalSat}:`,
        '',
        `"TOTAL Impuestos Fed SAT ${totalSat}"`,
        '',
        `Fecha de vencimiento: ${dueDateLong || resolvedDueDate}`,
        '',
        'Para cualquier duda puedes ponerte en contacto con nosotros en el teléfono o si lo deseas puedes escribirnos al correo.',
        '',
        'https://link.'
      ].join('\\n');
    }

    if (task?.service === 'DIOT') {
      const presentationDate = formatDueDateLong(metadata.diotPresentationDate) || metadata.diotPresentationDate || '—';
      const iva = metadata.diotIva || '0.00';
      const operation = metadata.diotOperation || '—';
      const type = metadata.diotType || 'Normal';
      const presentationMode = metadata.diotPresenta || 'Con datos';
      return [
        `Estimado, ${clientName}`,
        '',
        `Enviamos el link de la referencia para el DIOT ${periodLabel} (${type}) de la empresa ${empresa}. Folio: ${operation}.`,
        `La presenta: ${presentationMode}. IVA declarado: ${iva}.`,
        `Fecha de presentación: ${presentationDate}.`,
        '',
        `Fecha de vencimiento: ${dueDateLong || resolvedDueDate}`,
        '',
        'Para cualquier duda puedes ponerte en contacto con nosotros en el teléfono o si lo deseas puedes escribirnos al correo.',
        '',
        'https://link.'
      ].join('\\n');
    }

    if (task?.service === 'CE') {
      const operation = metadata.ceOperation || '—';
      const type = metadata.ceType || 'Normal';
      return [
        `Estimado, ${clientName}`,
        '',
        `Enviamos el link del comprobante de Contabilidad Electrónica ${periodLabel} (${type}) de la empresa ${empresa}.`,
        `No. de operación: ${operation}.`,
        '',
        `Fecha de vencimiento: ${dueDateLong || resolvedDueDate}`,
        '',
        'Para cualquier duda puedes ponerte en contacto con nosotros en el teléfono o si lo deseas puedes escribirnos al correo.',
        '',
        'https://link.'
      ].join('\\n');
    }

    if (task?.service === 'NOM') {
      const dueText = dueDateLong || resolvedDueDate || '—';
      return [
        `Estimado, ${clientName}`,
        '',
        `Enviamos el link de la o las nóminas del periodo ${periodLabel} de la empresa ${empresa}.`,
        '',
        `Fecha de vencimiento: ${dueText}`,
        '',
        'Para cualquier duda puedes ponerte en contacto con nosotros en el teléfono o si lo deseas puedes escribirnos al correo.',
        '',
        'https://link.'
      ].join('\\n');
    }

    return '';
  };

  return (
    <div
      className="overlay upload-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Cargar archivos para ${task?.client}`}
      onClick={onClose}
    >
      <form className="tax-upload-dialog" onSubmit={handleSubmit} onClick={event => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="eyebrow">Carga de evidencias</p>
            <h3>Adjuntar archivos</h3>
            <p className="subtitle">
              {task?.client} · {task?.obligation} · vence {task?.dueLabel || task?.dueText}
            </p>
          </div>
          <button type="button" className="link-btn" onClick={onClose}>
            Cerrar
          </button>
        </div>

        <div className="tax-upload-dialog-body">
          <div className="tax-upload-stepper">
            <div className={`tax-upload-step ${activeStep === STEP_UPLOAD ? 'active' : ''}`}>
              <span>1</span>
              <div>
                <strong>Paso 1</strong>
                <p>Carga de evidencia y datos clave</p>
              </div>
            </div>
            <div className={`tax-upload-step ${activeStep === STEP_NOTIFICATION ? 'active' : ''}`}>
              <span>2</span>
              <div>
                <strong>Paso 2</strong>
                <p>Notificación por correo</p>
              </div>
            </div>
          </div>

          <div className={`tax-upload-step-panel ${activeStep !== STEP_UPLOAD ? 'is-hidden' : ''}`}>
            {showInternalNotesSummary && (
              <div className="tax-note-summary">
                <div className="tax-note-summary-header">
                  <p className="eyebrow">Expediente completado</p>
                  <strong>Notas internas</strong>
                  <p className="helper-text">Estado de semáforo done · Estatus Completado</p>
                </div>
                {formattedInternalNotes.length ? (
                  <ul className="tax-note-summary-grid">
                    {formattedInternalNotes.map(item => (
                      <li key={item.key}>
                        <span className="label">{item.label}</span>
                        <strong>{item.value}</strong>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="helper-text">No hay JSON legible en Notas internas.</p>
                )}
                {!formattedInternalNotes.length && task?.notes && (
                  <p className="note-summary-raw">{task.notes}</p>
                )}
              </div>
            )}

            {!canEditFields && (
              <div className="tax-locked-card">
                <p className="label">Campos bloqueados</p>
                <p className="helper-text">
                  Este expediente está cerrado en modo lectura.
                </p>
              </div>
            )}

            {requiresUpload && (
              <section className="tax-legacy-upload-card">
                <div className="tax-legacy-upload-header">
                  <div>
                    <p className="label">Carga manual de expediente (beta)</p>
                    <small>Función en ajuste. Se prioriza la solicitud CFDI segura desde Drive.</small>
                  </div>
                  <button
                    type="button"
                    className={`info-toggle compact ${showLegacyUploadSection ? 'active' : ''}`}
                    onClick={() => setShowLegacyUploadSection(prev => !prev)}
                    aria-expanded={showLegacyUploadSection}
                  >
                    {showLegacyUploadSection ? 'Ocultar carga manual' : 'Mostrar carga manual'}
                  </button>
                </div>

                {showLegacyUploadSection && (
                  <>
                    <div
                      className={`tax-dropzone ${isDragging ? 'dragging' : ''}`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <input
                        type="file"
                        multiple
                        onChange={handleFileInput}
                        disabled={!canEditFields}
                      />
                      <p>
                        Arrastra tus archivos aquí o <span>explora tu equipo</span>
                      </p>
                      <small>
                        Máximo 10MB por archivo{isSatService ? ' · SAT admite hasta 2 archivos' : ''}
                      </small>
                    </div>

                    {!!selectedFiles.length && (
                      <ul className="tax-upload-file-list">
                        {selectedFiles.map((file, index) => (
                          <li key={`${file.name}-${file.lastModified || index}`}>
                            <span>{file.name}</span>
                            <small>{Math.round(file.size / 1024)} KB</small>
                            <button
                              type="button"
                              className="link-btn"
                              onClick={() => handleRemoveFile(index)}
                            >
                              Quitar
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </section>
            )}

            <div className="tax-requirements">
              <p className="label">Requisitos del expediente</p>
              <ul>
                {(task?.requirements || []).map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            {!requiresUpload && !allowsDriveUpload && (
              <p className="helper-text">Esta obligación no admite archivos en Drive.</p>
            )}
            {!requiresUpload && allowsDriveUpload && (
              <p className="helper-text">Esta actividad no solicita PDF.</p>
            )}

            {!!serviceFields.length && (
              <div className="tax-metadata-grid">
                {serviceFields.map(field => (
                  <label key={field.key}>
                    {field.label}
                    {field.type === 'select' ? (
                      <select
                        value={metadata[field.key] || ''}
                        onChange={event => handleMetadataChange(field.key, event.target.value)}
                        disabled={field.readOnly || !canEditFields}
                      >
                        <option value="">Selecciona</option>
                        {(field.options || []).map(option => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : field.type === 'checkbox' ? (
                      <input
                        type="checkbox"
                        checked={Boolean(metadata[field.key])}
                        onChange={event => handleMetadataChange(field.key, event.target.checked)}
                        disabled={field.readOnly || !canEditFields}
                      />
                    ) : (
                      <input
                        type={field.type}
                        value={metadata[field.key] || ''}
                        onChange={event => handleMetadataChange(field.key, event.target.value)}
                        placeholder={field.placeholder}
                        step={field.step}
                        readOnly={field.readOnly || !canEditFields}
                        disabled={field.readOnly || !canEditFields}
                      />
                    )}
                  </label>
                ))}
              </div>
            )}

            {!showInternalNotesSummary && task?.notes && <p className="helper-text">{task.notes}</p>}
            {showInternalNotesSummary && folderDisplay && (
              <p className="helper-text folder-url">
                <span className="folder-url-label">Carpeta asignada</span>
                {folderUrl ? (
                  <a href={folderUrl} target="_blank" rel="noreferrer" className="folder-url-link">
                    {folderUrl}
                  </a>
                ) : (
                  <span className="folder-url-link">{folderDisplay}</span>
                )}
              </p>
            )}
          </div>

          <div className={`tax-upload-step-panel ${activeStep !== STEP_NOTIFICATION ? 'is-hidden' : ''}`}>
            <div className="tax-upload-step-panel-header">
              <p className="eyebrow">Notificación final</p>
              <button type="button" className="link-btn" onClick={() => setActiveStep(STEP_UPLOAD)}>
                Volver a la carga
              </button>
            </div>

            {!!selectedFiles.length && (
              <p className="helper-text">
                {selectedFiles.length} archivo(s) listo(s) para revisar antes del envío.
              </p>
            )}

            {!!task?.service && (
              <div className="tax-email-preview">
                <p className="label">La información se enviará por e-mail a los siguientes correos:</p>
                <div className="email-recipient-list">
                  {emailRecipients.map(email => (
                    <span key={email} className="email-chip">
                      {email}
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => handleRemoveRecipient(email)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                {!emailRecipients.length && (
                  <p className="helper-text">Agrega al menos un destinatario para este aviso.</p>
                )}
                <div className="email-recipient-input">
                  <input
                    type="email"
                    placeholder="correo@empresa.com"
                    value={newRecipient}
                    onChange={event => setNewRecipient(event.target.value)}
                  />
                  <button type="button" className="btn ghost" onClick={handleAddRecipient}>
                    Agregar correo
                  </button>
                </div>

                <p className="label">Previsualización del mensaje</p>
                <textarea
                  readOnly
                  value={buildEmailPreview()}
                  rows={10}
                  className="email-preview-textarea"
                />
              </div>
            )}
          </div>

          {feedback && <p className="helper-text warning">{feedback}</p>}
        </div>

        <div className="modal-actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancelar
          </button>
          {activeStep === STEP_UPLOAD ? (
            <button
              type="button"
              className="btn primary"
              onClick={handleContinueToNotification}
              disabled={isSendingEvidence || !canEditFields}
              aria-busy={isSendingEvidence}
            >
              {isSendingEvidence
                ? 'Enviando evidencias...'
                : showLegacyUploadSection
                  ? 'Continuar a notificación'
                  : 'Continuar sin carga manual'}
            </button>
          ) : (
            <button type="submit" className="btn primary">
              Enviar a revisión
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default TaxUploadsBoard;
